import { useVirtualizer, VirtualItem } from "@tanstack/react-virtual";
import { RefObject, useCallback, useLayoutEffect, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import useRowKeyboardNavigation from "ds/components/Table/hooks/useKeyboardNavigation/useRowKeyboardNavigation";
import { calculateVirtualPadding } from "ds/components/Table/hooks/useStickyRow/helpers";
import useStickyRangeExtractor from "ds/components/Table/hooks/useStickyRow/useStickyRangeExtractor";
import { selectVisibleColumns } from "ds/components/Table/store/useTableStore/selectors";
import { OVERSCAN, ROW_HEIGHT_ESTIMATE } from "ds/components/TableSimple/constants";
import useTableSimpleEffectiveColumnPinning from "ds/components/TableSimple/hooks/useEffectiveColumnPinning";
import useTypedContext from "hooks/useTypedContext";
import { StackPullRequestItem } from "shared/Stack/useStackPullRequests/gql";
import { isAnsibleStackVendor } from "utils/stack";
import { StackContext } from "views/Stack/Context";

import { flattenStackPullRequestsTableRows } from "./helpers";
import StackPullRequestsTableRowPullRequest from "./Row/PullRequest";
import StackPullRequestsTableRowRun from "./Row/Run";
import StackPullRequestsTableStickyParentRow from "./StickyParentRow";
import styles from "./styles.module.css";
import useStackPullRequestsTableExpandedRowsStore from "./useExpandedRowsStore";
import useStackPullRequestsTableStore from "./useTableStore";

type StackPullRequestsTableRowsProps = {
  pullRequests: StackPullRequestItem[];
  tableRef: RefObject<HTMLDivElement | null>;
};

const StackPullRequestsTableRows = ({
  pullRequests,
  tableRef,
}: StackPullRequestsTableRowsProps) => {
  const { stack } = useTypedContext(StackContext);
  const isAnsible = isAnsibleStackVendor(stack);
  const expandedRows = useStackPullRequestsTableExpandedRowsStore((state) => state.expandedRows);
  const groupKeyboardProps = useRowKeyboardNavigation({ isGroup: true });
  const rowKeyboardProps = useRowKeyboardNavigation({ isGroup: false });

  const { columns, isFirstColumnPinned } = useStackPullRequestsTableStore(
    useShallow((state) => ({
      columns: selectVisibleColumns(state),
      isFirstColumnPinned: state.isFirstColumnPinned,
    }))
  );

  const { effectiveIsFirstColumnPinned } = useTableSimpleEffectiveColumnPinning(
    tableRef,
    isFirstColumnPinned
  );

  const { rows, stickyRanges } = useMemo(
    () => flattenStackPullRequestsTableRows(stack.id, pullRequests, expandedRows),
    [stack.id, pullRequests, expandedRows]
  );

  const hasExpandableRows = rows.some((row) => row.kind === "parent" && row.isExpandable);

  const { rangeExtractor, stickyGroupsVisibleOnTableRef } = useStickyRangeExtractor(stickyRanges);

  const getItemKey = useCallback(
    (index: number) => {
      const row = rows[index];
      return row.kind === "parent" ? row.pullRequest.id : `${row.pullRequest.id}-${row.run.id}`;
    },
    [rows]
  );

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableRef.current,
    estimateSize: () => ROW_HEIGHT_ESTIMATE,
    overscan: OVERSCAN,
    getItemKey,
    rangeExtractor,
  });

  useLayoutEffect(() => {
    virtualizer.measure();
  }, [rows.length, virtualizer]);

  const virtualizedItems = virtualizer.getVirtualItems();
  const [topGap, bottomGap] = calculateVirtualPadding(
    virtualizedItems,
    virtualizer.getTotalSize(),
    virtualizer.options.scrollMargin,
    stickyGroupsVisibleOnTableRef.current.size
  );

  const renderRow = (virtualRow: VirtualItem) => {
    const row = rows[virtualRow.index];

    if (row.kind === "child") {
      return (
        <StackPullRequestsTableRowRun
          key={virtualRow.key}
          pullRequest={row.pullRequest}
          run={row.run}
          stackId={stack.id}
          stackProvider={stack.provider}
          isAnsible={isAnsible}
          columns={columns}
          isFirstColumnPinned={effectiveIsFirstColumnPinned}
          keyboardProps={rowKeyboardProps}
          measureRef={virtualizer.measureElement}
          dataIndex={virtualRow.index}
        />
      );
    }

    if (row.isExpandable) {
      const stickyGroupEnd = stickyGroupsVisibleOnTableRef.current.get(virtualRow.index);
      const isActiveSticky = stickyGroupEnd !== undefined;
      const nextAfter = isActiveSticky
        ? virtualizer.measurementsCache[stickyGroupEnd + 1]
        : undefined;
      const nextAfterSticky = nextAfter
        ? nextAfter.start - (virtualizer.measurementsCache[virtualRow.index]?.size ?? 0) + 1
        : undefined;

      return (
        <StackPullRequestsTableStickyParentRow
          key={virtualRow.key}
          pullRequest={row.pullRequest}
          stackId={stack.id}
          stackProvider={stack.provider}
          isAnsible={isAnsible}
          isExpanded={row.isExpanded}
          columns={columns}
          isFirstColumnPinned={effectiveIsFirstColumnPinned}
          keyboardProps={groupKeyboardProps}
          measureRef={virtualizer.measureElement}
          dataIndex={virtualRow.index}
          isActiveSticky={isActiveSticky}
          nextAfterSticky={nextAfterSticky}
          tableRef={tableRef}
        />
      );
    }

    return (
      <StackPullRequestsTableRowPullRequest
        key={virtualRow.key}
        pullRequest={row.pullRequest}
        stackId={stack.id}
        stackProvider={stack.provider}
        isAnsible={isAnsible}
        columns={columns}
        isFirstColumnPinned={effectiveIsFirstColumnPinned}
        keyboardProps={rowKeyboardProps}
        measureRef={virtualizer.measureElement}
        dataIndex={virtualRow.index}
        className={hasExpandableRows ? styles.alignParent : undefined}
      />
    );
  };

  return (
    <>
      {topGap > 0 && <div style={{ display: "table-row", height: topGap }} aria-hidden="true" />}
      {virtualizedItems.map(renderRow)}
      {bottomGap > 0 && (
        <div style={{ display: "table-row", height: bottomGap }} aria-hidden="true" />
      )}
    </>
  );
};

export default StackPullRequestsTableRows;
